import { useEffect, useMemo, useState } from 'react';

const HERO_SLIDE_INTERVAL_MS = 9000;

function PageHero({
  className = '',
  mediaClassName = '',
  backgroundImage = '',
  backgroundImages = [],
  backgroundMedia = null,
  backgroundMediaItems = [],
  initialImageIndex = 0,
  isEditable = false,
  onEditBackground,
  overlay = null,
  children,
}) {
  const language =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('language') || 'en'
      : 'en';
  const wrapperClassName = ['page-hero', className].filter(Boolean).join(' ');
  const heroMediaClassName = ['page-hero__media', mediaClassName].filter(Boolean).join(' ');
  const heroMediaEntries = useMemo(() => {
    const mediaItems = Array.isArray(backgroundMediaItems)
      ? backgroundMediaItems.filter((mediaItem) => mediaItem?.src)
      : [];

    if (mediaItems.length) {
      return mediaItems;
    }

    if (backgroundMedia?.src) {
      return [backgroundMedia];
    }

    const imageUrls = Array.isArray(backgroundImages) ? backgroundImages.filter(Boolean) : [];

    if (imageUrls.length) {
      return imageUrls.map((src) => ({
        src,
        srcSet: '',
        sizes: '100vw',
      }));
    }

    return backgroundImage
      ? [
          {
            src: backgroundImage,
            srcSet: '',
            sizes: '100vw',
          },
        ]
      : [];
  }, [backgroundImage, backgroundImages, backgroundMedia, backgroundMediaItems]);
  const heroImageSignature = heroMediaEntries.map((entry) => entry.src).join('||');
  const resolvedInitialImageIndex =
    Number.isInteger(initialImageIndex) &&
    initialImageIndex >= 0 &&
    initialImageIndex < heroMediaEntries.length
      ? initialImageIndex
      : 0;
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [loadedImageIndexes, setLoadedImageIndexes] = useState([]);

  useEffect(() => {
    if (!heroMediaEntries.length) {
      setActiveImageIndex(0);
      setLoadedImageIndexes([]);
      return;
    }

    const initialIndexes =
      heroMediaEntries.length > 1
        ? [
            resolvedInitialImageIndex,
            (resolvedInitialImageIndex + 1) % heroMediaEntries.length,
          ]
        : [resolvedInitialImageIndex];

    setActiveImageIndex(resolvedInitialImageIndex);
    setLoadedImageIndexes([...new Set(initialIndexes)]);
  }, [heroImageSignature, heroMediaEntries.length, resolvedInitialImageIndex]);

  useEffect(() => {
    if (heroMediaEntries.length < 2) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveImageIndex((currentIndex) => (currentIndex + 1) % heroMediaEntries.length);
    }, HERO_SLIDE_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [heroImageSignature, heroMediaEntries.length]);

  useEffect(() => {
    if (heroMediaEntries.length < 2) {
      return;
    }

    const nextIndex = (activeImageIndex + 1) % heroMediaEntries.length;
    setLoadedImageIndexes((current) =>
      current.includes(nextIndex) ? current : [...current, nextIndex]
    );
  }, [activeImageIndex, heroImageSignature, heroMediaEntries.length]);

  return (
    <section className={wrapperClassName}>
      <div className={heroMediaClassName}>
        {heroMediaEntries.length ? (
          <div className="page-hero__media-layers" aria-hidden="true">
            {heroMediaEntries.map((mediaItem, index) =>
              loadedImageIndexes.includes(index) ? (
                <div
                  key={`${mediaItem.src}-${index}`}
                  className={`page-hero__media-layer${
                    index === activeImageIndex ? ' is-active' : ''
                  }`}
                >
                  <img
                    className="page-hero__media-image"
                    src={mediaItem.src}
                    srcSet={mediaItem.srcSet || undefined}
                    sizes={mediaItem.sizes || undefined}
                    style={{
                      '--page-hero-desktop-position': mediaItem.objectPosition || '50% 50%',
                      '--page-hero-mobile-position':
                        mediaItem.mobileObjectPosition ||
                        mediaItem.objectPosition ||
                        '50% 50%',
                      '--page-hero-desktop-scale': String(Number(mediaItem.zoom) || 1),
                      '--page-hero-mobile-scale': String(
                        Number(mediaItem.mobileZoom) || Number(mediaItem.zoom) || 1
                      ),
                    }}
                    alt=""
                    loading={index === resolvedInitialImageIndex ? 'eager' : 'lazy'}
                    fetchpriority={index === resolvedInitialImageIndex ? 'high' : 'auto'}
                    decoding="async"
                  />
                </div>
              ) : null
            )}
          </div>
        ) : null}
        {overlay ? <div className="page-hero__overlay">{overlay}</div> : null}
        {isEditable ? (
          <div className="page-hero__admin-actions">
            <button type="button" onClick={onEditBackground}>
              {language === 'ee' ? 'Muuda päist' : 'Edit hero'}
            </button>
          </div>
        ) : null}
        {children ? <div className="page-hero__content">{children}</div> : null}
      </div>
    </section>
  );
}

export default PageHero;
